import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BANK_TRANSACTION_BY_ID,
  FIND_ALL_TRANSACTIONS_TOTAL,
  FIND_BANK_TRANSACTIONS,
  FIND_UNMATCHED_STATEMENTS,
  GET_BANK_ACCOUNT_BY_ID,
  GET_CREDIT_CARD_BY_ID,
  LIST_BANK_ACCOUNTS,
  LIST_CREDIT_CARDS,
  LIST_MATCHED_PAYMENT_FOR_STATEMENTS,
  TRANSACTION_CATEGORIES,
} from '../graphql/queries/banking.query';
import {
  CATEGORIZE_TRANSACTION,
  CREATE_BANK,
  CREATE_CREDIT_CARD,
  CREATE_TRANSACTION,
  MATCH_OR_UNMATCH_STATEMENT,
  REMOVE_BANK_TRANSACTION,
  UPDATE_BANK,
  UPDATE_CREDIT_CARD,
  UPDATE_TRANSACTION,
} from '../graphql/mutation/banking.mutation';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BankingService {
  constructor(private apollo: Apollo) {}

  createBank(createBankAccountInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CREATE_BANK,
        variables: {
          createBankAccountInput: createBankAccountInput,
        },
      })
      .pipe(map((result: any) => result.data.createBankAccount || []));
  }

  createCreditCard(createCreditCardInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CREATE_CREDIT_CARD,
        variables: {
          createCreditCardInput: createCreditCardInput,
        },
      })
      .pipe(map((result: any) => result.data.createCreditCard || []));
  }

  updateBank(updateBankAccountInput: any, id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: UPDATE_BANK,
        variables: {
          id: id,
          updateBankAccountInput: updateBankAccountInput,
        },
      })
      .pipe(map((result: any) => result.data.updateBankAccount || []));
  }

  updateCreditCard(updateCreditCardInput: any, id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: UPDATE_CREDIT_CARD,
        variables: {
          id: id,
          updateCreditCardInput: updateCreditCardInput,
        },
      })
      .pipe(map((result: any) => result.data.updateCreditCard || []));
  }

  bulkCreateTransactions(file: File, offsetAccountId: any): Observable<any> {
    const token = localStorage.getItem('AUTH_TOKEN');
    const formData = new FormData();
    formData.append(
      'operations',
      JSON.stringify({
        query: `
          mutation bulkCreateStatements($file: Upload!, $offsetAccountId: Int!) {
            bulkCreateStatements(file: $file, offsetAccountId: $offsetAccountId)
          }
          `,
        variables: { file: null, offsetAccountId },
      })
    );

    formData.append('map', JSON.stringify({ '0': ['variables.file'] }));
    formData.append('0', file);

    return from(
      fetch(environment.url.accountsUrl, {
        method: 'POST',
        body: formData,
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          'x-apollo-operation-name': 'uploadFile',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => result.data?.bulkCreateTransactions || result)
    );
  }

  listAllBankAccounts(search: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: LIST_BANK_ACCOUNTS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.bankAccounts || []));
  }

  listAllCreditCards(search: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: LIST_CREDIT_CARDS,
        variables: {
          search,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.creditCards || []));
  }

  getBankAccountById(id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_BANK_ACCOUNT_BY_ID,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.bankAccount || []));
  }

  getCreditCardById(id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: GET_CREDIT_CARD_BY_ID,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.creditCard || []));
  }

  findUnmatchedStatements(
    bankAccountId: any,
    search: any,
    startDate: any,
    endDate: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: FIND_UNMATCHED_STATEMENTS,
        variables: {
          bankAccountId,
          search,
          startDate,
          endDate,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findUnmatchedStatements || [])
      );
  }

  findAllTransactionTotals(
    bankAccountId: any,
    search: any,
    startDate: any,
    endDate: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: FIND_ALL_TRANSACTIONS_TOTAL,
        variables: {
          bankAccountId,
          search,
          startDate,
          endDate,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.findAllTransactionTotals || [])
      );
  }

  bankTransactions(
    bankAccountId: any,
    search: any,
    startDate: any,
    endDate: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: FIND_BANK_TRANSACTIONS,
        variables: {
          bankAccountId,
          search,
          startDate,
          endDate,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.bankTransactions || []));
  }

  listMatchedPaymentsForStatement(statementId: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: LIST_MATCHED_PAYMENT_FOR_STATEMENTS,
        variables: {
          statementId,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.listMatchedPaymentsForStatement || [])
      );
  }

  matchOrUnmatchStatementToTransactions(
    statusType: string,
    statementId: number,
    transactionIds: number[]
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: MATCH_OR_UNMATCH_STATEMENT,
        variables: {
          statusType,
          statementId,
          transactionIds,
        },
      })
      .pipe(
        map(
          (result: any) =>
            result.data.matchOrUnmatchStatementToTransactions || []
        )
      );
  }

  createBankTransaction(createBankTransactionInput: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CREATE_TRANSACTION,
        variables: {
          createBankTransactionInput,
        },
      })
      .pipe(
        map((result: any) => {
          result.data.createBankTransaction || [];
        })
      );
  }

  transactionCategories(direction: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: TRANSACTION_CATEGORIES,
        variables: {
          direction,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map((result) => result.data?.transactionCategories || [])
      );
  }

  categorizeTransactions(
    statusType: any,
    statementId: any,
    transactionId: any,
    createTransactionInput: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: CATEGORIZE_TRANSACTION,
        variables: {
          statusType,
          statementId,
          transactionId,
          createTransactionInput,
        },
      })
      .pipe(
        map((result: any) => {
          result.data.categorizeTransactions || [];
        })
      );
  }

  bankTransaction(id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .watchQuery<any>({
        query: BANK_TRANSACTION_BY_ID,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map((result) => result.data?.bankTransaction || []));
  }

  updateBankTransaction(
    id: any,
    updateBankTransactionInput: any
  ): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: UPDATE_TRANSACTION,
        variables: {
          id,
          updateBankTransactionInput,
        },
      })
      .pipe(
        map((result: any) => {
          result.data.updateBankTransaction || [];
        })
      );
  }

  removeBankTransaction(id: any): Observable<any> {
    return this.apollo
      .use('accounts')
      .mutate({
        mutation: REMOVE_BANK_TRANSACTION,
        variables: {
          id,
        },
      })
      .pipe(
        map((result: any) => {
          result.data.removeBankTransaction || [];
        })
      );
  }
}
